import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityColor = {
  CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/20',
  HIGH:     'bg-orange-500/10 text-orange-400 border-orange-500/20',
  MEDIUM:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  LOW:      'bg-green-500/10 text-green-400 border-green-500/20',
};

const flagLabels = {
  same_pickup_delivery_zip:  'Same pickup/delivery ZIP',
  short_pickup_window:       'Short pickup window (<72h)',
  non_continental_us:        'Alaska/Hawaii (non-continental)',
  disposable_email:          'Disposable email address',
  invalid_phone:             'Invalid phone number',
  multiple_bookings_same_ip: 'Multiple bookings from same IP (30 min)',
  rapid_velocity_same_ip:    'Rapid velocity: 3+ requests in 5 min',
  repeat_offender:           'Repeat offender (previous fraud score)',
};

export default function FraudCaseDetailPage() {
  const { caseId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [fraudCase, setFraudCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/fraud/cases/${caseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setFraudCase(await res.json());
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
  }, [caseId]);

  const handleReview = async () => {
    if (!action) { setError('Please select an action.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/fraud/cases/${caseId}/review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes }),
      });
      if (res.ok) {
        const data = await res.json();
        if (action === 'approve' && data.load_id) {
          navigate(`/admin/loads/${data.load_id}`);
        } else {
          navigate('/admin/fraud/cases');
        }
      } else {
        const data = await res.json();
        setError(data.detail || 'Review failed.');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" />
    </div>
  );

  if (!fraudCase) return <p className="text-muted-foreground">Case not found.</p>;

  const rawFormData = fraudCase.raw_form_data || {};
  const flags = fraudCase.fraud_flags || [];
  const isResolved = ['approved', 'rejected', 'escalated'].includes(fraudCase.status);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="fraud-case-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/admin/fraud/cases">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
          </Button>
        </Link>
        <h1 className="font-outfit text-2xl font-semibold">Fraud Case</h1>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${severityColor[fraudCase.severity] || ''}`}>{fraudCase.severity}</span>
      </div>

      {/* Lead info */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Customer</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-muted-foreground">Name</span><p className="font-medium">{fraudCase.name || '—'}</p></div>
          <div><span className="text-muted-foreground">Email</span><p className="font-medium">{fraudCase.email || '—'}</p></div>
          <div><span className="text-muted-foreground">Phone</span><p className="font-medium">{fraudCase.phone || '—'}</p></div>
          <div><span className="text-muted-foreground">IP Address</span><p className="font-mono">{fraudCase.ip_address || '—'}</p></div>
          {rawFormData.pickup_zip && <div><span className="text-muted-foreground">Pickup ZIP</span><p>{rawFormData.pickup_zip}</p></div>}
          {rawFormData.delivery_zip && <div><span className="text-muted-foreground">Delivery ZIP</span><p>{rawFormData.delivery_zip}</p></div>}
        </div>
      </div>

      {/* Fraud score & flags */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Fraud Analysis</h2>
          <span className="font-outfit text-2xl font-bold text-accent">{fraudCase.fraud_score}</span>
        </div>
        <div className="space-y-2">
          {flags.map((flag) => (
            <div key={flag} className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 shrink-0" strokeWidth={1.5} />
              <span>{flagLabels[flag] || flag}</span>
            </div>
          ))}
          {flags.length === 0 && <p className="text-muted-foreground text-sm">No flags.</p>}
        </div>
      </div>

      {/* Review section */}
      {isResolved ? (
        <div className="bg-card border border-border rounded-xl p-5 space-y-2">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Resolution</h2>
          <p className="text-sm capitalize"><span className="text-muted-foreground">Status:</span> {fraudCase.status}</p>
          {fraudCase.reviewer_email && <p className="text-sm"><span className="text-muted-foreground">Reviewed by:</span> {fraudCase.reviewer_email}</p>}
          {fraudCase.resolution_notes && <p className="text-sm"><span className="text-muted-foreground">Notes:</span> {fraudCase.resolution_notes}</p>}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Review Action</h2>
          <div className="flex gap-3">
            {['approve', 'reject', 'escalate'].map((a) => (
              <button
                key={a}
                onClick={() => setAction(a)}
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors capitalize ${
                  action === a
                    ? a === 'approve' ? 'bg-green-500/20 border-green-500/40 text-green-400'
                      : a === 'reject' ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : 'bg-purple-500/20 border-purple-500/40 text-purple-400'
                    : 'bg-muted border-border text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          <textarea
            className="w-full bg-muted border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
            rows={3}
            placeholder="Optional notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <Button
            onClick={handleReview}
            disabled={!action || submitting}
            className="w-full"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      )}
    </div>
  );
}
